import { Button, InputNumber, Modal, Popover, Row, Select, Spin, Table, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import "@styles/styles-FixedValuesTab.css";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import {
	deleteDayTarget, getDayTargetReferenceValues, getDayTargetSuggestions, getDayTargetValues, saveDayTarget,
} from "@store/actions/dayTargets.actions";
import {
	CALCULATOR_SOURCES, PEAK_METRICS, TOTAL_TIME_KEY, applyCalculator, applyCellEdit, applyPositionEdit,
	buildTargetRows, canApply, clearValues, errorText, maxValueFor, editorColumns, groupByPosition, hasBlankCell, marksAfterEdit,
	roundValue, rowChanged, sourceLabels, toPayload, valuesDiffer,
} from "@utils/dayTargets.mjs";

type PositionGroup = { positionId: number | null; name: string; players: { id: string; name: string }[] };

const { Text } = Typography;

type Values = Record<string, Record<string, number | null>>;
type Marks = Record<string, Record<string, string>>;
type Scope = { player_id: string } | { position_id: number } | { scope: "team" };

const FALLBACK_SOURCES: Record<string, string> = { position: "the position average", team: "the team average", zero: "0" };

/**
 * The "Fixed values" tab of the day target dialog: one value for each player and visible metric.
 * Players sit under their position; a position row edits all its players, and the team row only
 * shows the result. A new day opens pre-filled with the average of the last 4 same weekdays' data.
 * Saving creates (POST) or replaces (PUT) the day's fixed values.
 */
export const FixedValuesTab = ({ dateString, mode, onDirtyChange, onSaved, onDeleted, onCancel }: IFixedValuesTab) => {
	const dispatch = useDispatch<any>();
	const teamMetrics = useSelector(({ team }: any) => team.metrics);
	const teamPlayers = useSelector(({ team }: any) => team.players);
	const date = dayjs(dateString).format("YYYY-MM-DD");
	const weekday = dayjs(dateString).format("ddd");

	const columns = useMemo(() => editorColumns(teamMetrics), [teamMetrics]);
	const positions = useMemo(() => groupByPosition(teamPlayers) as PositionGroup[], [teamPlayers]);
	const playerIds = useMemo(() => positions.flatMap((position) => position.players.map((player) => player.id)), [positions]);

	const [loading, setLoading] = useState(true);
	// After a failed load the table is empty, so it must never be applied or saved.
	const [loadFailed, setLoadFailed] = useState(false);
	const [saving, setSaving] = useState(false);
	const [values, setValues] = useState<Values>({});
	const [loaded, setLoaded] = useState<Values>({});
	const [snapshot, setSnapshot] = useState<Values>({});
	const [marks, setMarks] = useState<Marks>({});
	// The marks of the snapshot, so a cell typed back to its snapshot value gets its mark back.
	const [snapshotMarks, setSnapshotMarks] = useState<Marks>({});
	// Set by a save with blank cells; the blank cells then show a red border until they get a value.
	const [showBlanks, setShowBlanks] = useState(false);
	const [source, setSource] = useState("weekday_data");
	const [percent, setPercent] = useState<number | null>(100);
	// A new day is pre-filled with weekday_data × 100%. Saved values come from no calculator choice.
	const [applied, setApplied] = useState<{ source: string | null; percent: number | null }>(
		mode === "edit" ? { source: null, percent: null } : { source: "weekday_data", percent: 100 });

	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			setLoading(true);
			try {
				const response = mode === "edit"
					? await dispatch(getDayTargetValues(date))
					: await dispatch(getDayTargetReferenceValues(date, "weekday_data"));
				if (cancelled) return;
				setLoadFailed(false);
				const initial = response?.values ?? {};
				setValues(initial);
				setLoaded(initial);
				setSnapshot(initial);
				const initialMarks = (mode === "edit" ? response?.derived : response?.fallback) ?? {};
				setMarks(initialMarks);
				setSnapshotMarks(initialMarks);
			} catch (e) {
				if (!cancelled) setLoadFailed(true);
				if (!cancelled) message.error(errorText(e, "Error loading day targets"));
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		load();
		return () => { cancelled = true; };
	}, [date, mode]);

	const dirty = useMemo(() => valuesDiffer(values, loaded, playerIds, columns), [values, loaded, playerIds, columns]);
	useEffect(() => { onDirtyChange?.(dirty); }, [dirty]);

	const applyEnabled = percent != null && canApply({ source, percent, applied, values, snapshot, playerIds, columns });
	const payload = useMemo(() => toPayload(values, playerIds, columns), [values, playerIds, columns]);

	const editCell = (rowKind: string, rowId: string, metricKey: string, value: number | null, rowPlayers: string[]) => {
		const decimals = columns.find((column: any) => column.key === metricKey)?.decimals ?? 0;
		const ids = rowKind === "position" ? rowPlayers : [rowId];
		const next = rowKind === "position"
			? applyPositionEdit(values, rowPlayers, metricKey, value)
			: applyCellEdit(values, rowId, metricKey, value);
		setValues(next);
		setMarks((current) => marksAfterEdit(current, snapshotMarks, next, snapshot, ids, metricKey, decimals));
	};

	const applyToTable = () => {
		const labels = sourceLabels(weekday) as Record<string, string>;
		Modal.confirm({
			centered: true,
			title: "Replace all values in the table?",
			content: `"${labels[source]}" × ${percent}% replaces the value of every player for every metric. Your edits in the table are lost.`,
			okText: "Replace values",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					const response = await dispatch(getDayTargetReferenceValues(date, source));
					const next = applyCalculator(values, response?.values ?? {}, percent as number, columns, playerIds);
					setValues(next);
					setSnapshot(next);
					setMarks(response?.fallback ?? {});
					setSnapshotMarks(response?.fallback ?? {});
					setShowBlanks(false);
					setApplied({ source, percent });
				} catch (e) {
					message.error(errorText(e, "Error loading the calculator values"));
				}
			},
		});
	};

	const save = async () => {
		if (columns.some((column: any) => hasBlankCell(values, playerIds, column.key))) {
			setShowBlanks(true);
			message.error("Every cell needs a value before you save. 0 is a valid value.");
			return;
		}
		setSaving(true);
		try {
			await dispatch(saveDayTarget(date, payload, mode));
			message.success("Day targets saved!");
			onSaved?.();
		} catch (e) {
			message.error(errorText(e, "Error saving day targets"));
		} finally {
			setSaving(false);
		}
	};

	const deleteTargets = () => {
		Modal.confirm({
			centered: true,
			title: "Delete this day target?",
			content: `This deletes the targets of all players for ${dayjs(dateString).format("ddd, MMM D")}. You cannot undo this.`,
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					await dispatch(deleteDayTarget(date));
					message.success("Day target deleted");
					onDeleted?.();
				} catch (e) {
					message.error(errorText(e, "Error deleting day targets"));
				}
			},
		});
	};

	const clearTargets = () => {
		setValues(clearValues(playerIds, columns));
		setMarks({});
		setShowBlanks(false);
	};

	const tableRows = useMemo(() => buildTargetRows(positions, values, columns, marks), [positions, values, columns, marks]);

	// A position with no id ("No position") has no scope the backend knows, so it gets no popover.
	const scopeOf = (row: any): Scope | null =>
		row.kind === "team" ? { scope: "team" }
			: row.kind === "position" ? (row.positionId == null ? null : { position_id: row.positionId })
				: { player_id: row.id };

	// A row's value is a fallback when none of its players has a value of their own for the metric.
	const fallbackNote = (row: any, key: string) => {
		const sources = row.players.map((id: string) => marks[id]?.[key]);
		if (!sources.length || sources.some((source: string | undefined) => !source)) return null;
		const shown = sources.every((source: string) => source === sources[0]) ? FALLBACK_SOURCES[sources[0]] : null;
		const who = row.kind === "player" ? "This player has" : "These players have";
		return `${who} no value of their own, so the table shows ${shown ?? "the position or team average"}.`;
	};

	const tableColumns = [
		{ title: "Position", dataIndex: "name", key: "name", fixed: "left" as const, width: 200 },
		...columns.map((column: any) => ({
			title: column.name,
			key: column.key,
			align: "right" as const,
			render: (_: any, row: any) => {
				const value = row.values?.[column.key];
				const scope = scopeOf(row);
				const info = scope && (
					<SuggestionPopover
						date={date}
						column={column}
						rowName={row.name}
						scope={scope}
						currentValue={value}
						note={fallbackNote(row, column.key)}
						onUse={row.kind === "team" ? undefined
							: (suggested: number) => editCell(row.kind, row.id, column.key, suggested, row.players)}
					/>
				);
				if (row.kind === "team") {
					return <span className="day-targets-muted">{info} {value ?? "—"}</span>;
				}
				// Blue marks a cell that shows a value other than at the last load or "Apply to table".
				const changed = rowChanged(values, snapshot, row.players, column);
				const blank = showBlanks && hasBlankCell(values, row.players, column.key);
				const inputClass = blank ? "day-targets-blank" : changed ? "day-targets-changed" : "";
				return (
					<span className="day-targets-cell">
						{info}{" "}
						<InputNumber
							size="small"
							min={0}
							max={maxValueFor(column.decimals)}
							precision={column.decimals}
							value={value ?? null}
							onChange={(next) => editCell(row.kind, row.id, column.key, next as number | null, row.players)}
							className={`day-targets-input ${inputClass}`.trim()}
						/>
					</span>
				);
			},
		})),
	];

	const labels = sourceLabels(weekday) as Record<string, string>;
	return (
		<Spin spinning={loading}>
			<div className="day-targets-editor">
				<Text type="secondary">Values are per player. A new day is pre-filled with the {labels.weekday_data.replace(" (data)", "")} data.</Text>
				<Row className="day-targets-calculator">
					<b>Calculator</b>
					<Select
						value={source}
						onChange={setSource}
						className="day-targets-source"
						options={CALCULATOR_SOURCES.map((key) => ({ value: key, label: labels[key] }))}
					/>
					×
					<InputNumber min={0} max={1000} value={percent} onChange={(next) => setPercent(next)} className="day-targets-percent" />
					%
					<Button type="primary" disabled={!applyEnabled || loading || loadFailed} onClick={applyToTable}>Apply to table</Button>
					<Text type="secondary" className="day-targets-full-row">
						Each player gets their own value × the percent, for every metric. This replaces every value in the table.
					</Text>
				</Row>
				<Table
					size="small"
					pagination={false}
					scroll={{ x: "max-content", y: 330 }}
					columns={tableColumns as any}
					dataSource={tableRows}
				/>
				<Row className="day-targets-footer">
					<Button onClick={clearTargets} className="day-targets-clear">Clear targets</Button>
					{mode === "edit" && <Button danger onClick={deleteTargets}>Delete targets</Button>}
					<Button onClick={onCancel}>Cancel</Button>
					<Button type="primary" loading={saving} disabled={loading || loadFailed || !Object.keys(payload).length} onClick={save}>Save targets</Button>
				</Row>
			</div>
		</Spin>
	);
};

/** The ⓘ popover of one cell: the row's reference values for one metric, fetched on click. */
const SuggestionPopover = ({ date, column, rowName, scope, currentValue, note, onUse }: ISuggestionPopover) => {
	const dispatch = useDispatch<any>();
	const [open, setOpen] = useState(false);
	const [data, setData] = useState<any>(null);
	const [loading, setLoading] = useState(false);

	const onOpenChange = async (next: boolean) => {
		setOpen(next);
		if (!next || data || loading) return;
		setLoading(true);
		try {
			setData(await dispatch(getDayTargetSuggestions(date, column.key, scope)));
		} catch (e) {
			message.error(errorText(e, "Error loading the suggestions"));
		} finally {
			setLoading(false);
		}
	};

	const percentOf = (value: number | null) =>
		value && currentValue != null ? ` (${Math.round((currentValue / value) * 100)}%)` : "";
	const isGroup = !("player_id" in scope);
	const content = loading || !data ? <Spin size="small" /> : (
		<div className="day-targets-popover">
			<b>{rowName.replace(/ \(\d+\)$/, "")}{isGroup ? ` (${data.player_count} players)` : ""} · {column.name}</b>
			<Text type="secondary">
				Here are some of the {isGroup ? `players' ${column.name} values, as ${PEAK_METRICS.includes(column.key) ? "the maximum" : "an average"} across players` : `${column.name} values`}.
			</Text>
			<Row className="day-targets-popover-row"><span>Current value</span><b>{currentValue ?? "—"}</b></Row>
			{note && <Text type="secondary">{note}</Text>}
			<div className="day-targets-popover-divider" />
			{data.rows.map((row: any) => (
				<Row key={row.key} className="day-targets-popover-row">
					<span>{row.label}</span><span><b>{row.value ?? "—"}</b><Text type="secondary">{percentOf(row.value)}</Text></span>
				</Row>
			))}
			{column.key === TOTAL_TIME_KEY && data.weekdays.map((day: any) => (
				<Row key={day.date} className="day-targets-popover-row">
					<span>{day.label}</span>
					<span><b>{day.value ?? "—"}</b><Text type="secondary">{day.value != null ? percentOf(day.value) : " (no data)"}</Text></span>
				</Row>
			))}
			{data.recommendation?.value != null
				&& roundValue(data.recommendation.value, column.decimals) !== roundValue(currentValue, column.decimals) && (
				<div className="day-targets-recommendation">
					Based on this data, Whistle recommends {data.recommendation.percent}% of {data.recommendation.label}:{" "}
					{onUse ? (
						<a onClick={() => { onUse(data.recommendation.value); setOpen(false); }}><u>{data.recommendation.value}</u></a>
					) : <b>{data.recommendation.value}</b>}
				</div>
			)}
		</div>
	);

	return (
		<Popover trigger="click" open={open} onOpenChange={onOpenChange} content={content} placement="bottomLeft">
			<InfoCircleOutlined aria-label={`Suggestions for ${rowName}, ${column.name}`} className="day-targets-info" />
		</Popover>
	);
};

interface IFixedValuesTab {
	dateString: string;
	mode: "create" | "edit";
	onDirtyChange?: (dirty: boolean) => void;
	onSaved?: () => void;
	onDeleted?: () => void;
	onCancel: () => void;
}

interface ISuggestionPopover {
	date: string;
	column: { key: string; name: string; decimals: number };
	rowName: string;
	scope: Scope;
	currentValue?: number | null;
	note?: string | null;
	onUse?: (value: number) => void;
}

export default FixedValuesTab;
